import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import * as api from '../api/client';
import type { DemoPartner } from '../types';

interface AuthState {
  partner: DemoPartner | null;
  loading: boolean;
  /** Refresh the cached partner from /me. */
  refresh: () => Promise<void>;
  /** Update local partner state after a branding write returns the new shape. */
  setPartner: (partner: DemoPartner) => void;
  /** Persist a session JWT and load the matching partner. */
  authenticate: (jwt: string, partner: DemoPartner) => void;
  /** Clear local session state. */
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [partner, setPartnerState] = useState<DemoPartner | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Session expiry: an authenticated request answered 401 (the client already
  // cleared the token) ends the local session and says so on the sign-in page
  // — pages used to sit on raw "Unauthorized" errors under a signed-in header.
  // Cached queries go too, so no partner's data leaks into the next session.
  useEffect(() => {
    api.setUnauthorizedHandler(() => {
      setPartnerState(null);
      setLoading(false);
      queryClient.clear();
      navigate('/login?session=expired', { replace: true });
    });
    return () => api.setUnauthorizedHandler(null);
  }, [navigate, queryClient]);

  const refresh = useCallback(async () => {
    if (!api.getStoredToken()) {
      setPartnerState(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.getMe();
      setPartnerState(me);
    } catch {
      api.clearStoredToken();
      setPartnerState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const authenticate = useCallback((jwt: string, p: DemoPartner) => {
    api.setStoredToken(jwt);
    setPartnerState(p);
    setLoading(false);
  }, []);

  const signOut = useCallback(() => {
    void api.signOut();
    api.clearStoredToken();
    setPartnerState(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      partner,
      loading,
      refresh,
      setPartner: setPartnerState,
      authenticate,
      signOut,
    }),
    [partner, loading, refresh, authenticate, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
